import { Box, Button, Card, Divider, Group, Stack, Text, VisuallyHidden } from '@mantine/core';
import { IconArrowRight, IconChevronRight, IconRefresh } from '@tabler/icons-react';
import { Link } from 'react-router';
import { formatCompactSignedMoney } from 'src/common/currency';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { ScenarioScope } from '../scope';
import { ModelGroup, peopleIn } from './groups';
import { LongevityMeter, MeterScale } from './LongevityMeter';
import { ModelMenu } from './ModelMenu';
import classes from './ModelList.module.css';
import { legacyFigure, legacyLabel, modelOutcome, OutcomeStatus, planScale } from './modelOutcome';
import {
  joinNames,
  ModelMeta,
  modelTarget,
  PeopleAvatars,
  PendingSyncNotice,
  PrimaryMark,
} from './modelParts';
import { OutcomeVerdict, STATUS_STYLE } from './OutcomeVerdict';
import { modelTimeline } from './timeline';

type Props = {
  group: ModelGroup;
  names: Record<string, string>;
  scope: ScenarioScope;
  // Shows the group's other models in the All models view.
  onViewOthers: () => void;
  onViewArchived: () => void;
};

const primaryLabel = (joint: boolean, people: string[]): string => {
  if (joint) return 'Joint primary model';
  return people[0] ? `${people[0]}'s primary model` : 'Primary model';
};

/** A group's primary model, in full, with a count of the other models measured against it. */
export function PrimaryModelCard({ group, names, scope, onViewOthers, onViewArchived }: Props) {
  const currency = useCurrency();
  const people = peopleIn(group, names);
  const primary = group.primary;

  if (primary == null) {
    return (
      <Card withBorder radius="lg" p="lg" className={classes.row} data-testid="primary-model-card">
        <Group gap="xs" mb="sm" wrap="nowrap">
          {people.length > 0 && <PeopleAvatars names={people} />}
          <Text size="sm" fw={600}>
            {people.length > 0
              ? `${joinNames(people)} ${people.length > 1 ? 'have' : 'has'} no primary model`
              : 'No primary model'}
          </Text>
        </Group>
        <OtherModels group={group} onView={onViewOthers} />
      </Card>
    );
  }

  const kpis = primary.kpis;
  const outcome = modelOutcome(kpis);
  const timeline = modelTimeline(kpis);

  return (
    <Card
      withBorder
      radius="lg"
      p="lg"
      className={classes.row}
      style={{ borderColor: 'var(--mantine-color-brand-1)' }}
      data-testid="primary-model-card"
    >
      <Group justify="space-between" align="flex-start" gap="lg" wrap="nowrap">
        <Stack gap={4} miw={0}>
          <Group gap="xs" wrap="nowrap">
            <PrimaryMark label={primaryLabel(group.joint, people)} />
            {people.length > 0 && <PeopleAvatars names={people} />}
            {group.joint && people.length > 0 && (
              <Text size="xs" c="dimmed">
                {joinNames(people)}
              </Text>
            )}
          </Group>
          <Link to={modelTarget(primary, scope)} className={classes.modelLink}>
            <Text size="lg" fw={700} lineClamp={1}>
              {primary.label}
            </Text>
          </Link>
          <ModelMeta scenario={primary} />
        </Stack>
        <Group gap="xl" wrap="nowrap" align="flex-start">
          <Figure
            value={legacyFigure(primary, currency)}
            label={kpis ? `${legacyLabel(kpis)}, today's money` : null}
          />
          <Figure
            value={
              kpis?.peakNetWorthValue != null
                ? formatCompactSignedMoney(kpis.peakNetWorthValue, currency)
                : '—'
            }
            label="Peak net worth"
          />
          <Group gap="xxs" wrap="nowrap">
            <ModelMenu
              scenario={primary}
              scope={scope}
              group={group}
              names={names}
              onViewArchived={onViewArchived}
            />
            <IconChevronRight size={20} color="var(--mantine-color-gray-4)" aria-hidden />
          </Group>
        </Group>
      </Group>
      <Stack gap="xxs" mt="md">
        <OutcomeVerdict outcome={outcome} />
        {kpis && timeline && outcome.status !== 'notProjected' && (
          <>
            <LongevityMeter
              status={outcome.status}
              timeline={timeline}
              scale={timeline.length}
              size="lg"
            />
            <MeterScale labels={planScale(kpis, timeline)} scale={timeline.length} />
          </>
        )}
      </Stack>
      {primary.hasPendingSync && <PendingSyncNotice />}
      <Divider my="md" color="gray.2" />
      <OtherModels group={group} onView={onViewOthers} />
    </Card>
  );
}

function Figure({ value, label }: { value: string; label: string | null }) {
  return (
    <Box>
      <Text fz="displayXs" fw={700} lh={1.25}>
        {value}
      </Text>
      {label && (
        <Text size="xs" c="dimmed">
          {label}
        </Text>
      )}
    </Box>
  );
}

const COUNTED: {
  status: Exclude<OutcomeStatus, 'notProjected'>;
  one: string;
  many: string;
}[] = [
  {
    status: 'shortEarly',
    one: 'runs short within life expectancy',
    many: 'run short within life expectancy',
  },
  {
    status: 'shortLate',
    one: 'runs short after life expectancy',
    many: 'run short after life expectancy',
  },
  { status: 'lasts', one: 'lasts for life', many: 'last for life' },
];

/** How many other models the group has, and how their outcomes split. */
function OtherModels({ group, onView }: { group: ModelGroup; onView: () => void }) {
  const others = group.models.filter(model => model !== group.primary && !model.archived);
  if (others.length === 0) {
    return (
      <Text size="xs" c="dimmed">
        No other models
      </Text>
    );
  }
  const statuses = others.map(model => modelOutcome(model.kpis).status);
  const modelCount = `${others.length} ${group.primary ? 'other ' : ''}model${others.length === 1 ? '' : 's'}`;
  const toReview = others.filter(model => model.hasPendingSync).length;

  return (
    <Group justify="space-between" wrap="nowrap" data-testid="other-models-summary">
      <Group gap="sm" wrap="nowrap">
        <Text size="xs" c="dimmed">
          {modelCount}
        </Text>
        {COUNTED.map(({ status, one, many }) => {
          const count = statuses.filter(candidate => candidate === status).length;
          if (count === 0) return null;
          const { icon: StatusIcon, colour } = STATUS_STYLE[status];
          return (
            <Group key={status} gap={4} wrap="nowrap">
              <StatusIcon
                size={16}
                stroke={1.75}
                color={`var(--mantine-color-${colour}-7)`}
                aria-hidden
              />
              <Text size="xs" fw={600}>
                {count}
                <VisuallyHidden> {count === 1 ? one : many}</VisuallyHidden>
              </Text>
            </Group>
          );
        })}
        {toReview > 0 && (
          <Group gap={4} wrap="nowrap">
            <IconRefresh size={14} color="var(--mantine-color-brand-7)" aria-hidden />
            <Text size="xs" fw={600} c="brand.7">
              {toReview} to review
            </Text>
          </Group>
        )}
      </Group>
      <Button
        variant="subtle"
        size="compact-xs"
        rightSection={<IconArrowRight size={14} />}
        onClick={onView}
        className={classes.aboveLink}
        aria-label={`View ${modelCount}`}
      >
        View
      </Button>
    </Group>
  );
}
